import React from 'react';
import { Flag, Lock } from 'lucide-react';

interface Props {
  onSelect: (framework: string) => void;
}

const FrameworkSelection: React.FC<Props> = ({ onSelect }) => {
  const frameworks = [
    {
      id: 'canada',
      name: 'Canada',
      fullName: 'Canadian AI Framework',
      description: 'Based on AIDA (Bill C-27) and Voluntary Code of Conduct',
      icon: '🇨🇦',
      enabled: true,
      questions: 45,
    },
    {
      id: 'eu',
      name: 'European Union',
      fullName: 'EU AI Act',
      description: 'Comprehensive AI regulation framework',
      icon: '🇪🇺',
      enabled: false,
      questions: 0,
    },
    {
      id: 'us',
      name: 'United States',
      fullName: 'US AI Framework',
      description: 'NIST AI Risk Management Framework',
      icon: '🇺🇸',
      enabled: false,
      questions: 0,
    },
    {
      id: 'uk',
      name: 'United Kingdom',
      fullName: 'UK AI Framework',
      description: 'Pro-innovation approach to AI regulation',
      icon: '🇬🇧',
      enabled: false,
      questions: 0,
    },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-purple-50 to-pink-50 p-8">
      <div className="max-w-6xl mx-auto fade-in">
        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="text-5xl font-bold mb-4">
            <span className="gradient-text">Select Framework</span>
          </h1>
          <p className="text-xl text-gray-600">
            Choose the regulatory framework for your assessment
          </p>
        </div>

        {/* Progress Indicator */}
        <div className="flex items-center justify-center gap-4 mb-12">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 bg-green-500 text-white rounded-full flex items-center justify-center font-semibold">
              ✓
            </div>
            <span className="text-sm font-medium text-gray-700">Landing</span>
          </div>
          <div className="w-16 h-1 bg-indigo-300"></div>
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 bg-indigo-600 text-white rounded-full flex items-center justify-center font-semibold">
              2
            </div>
            <span className="text-sm font-medium text-gray-700">Framework</span>
          </div>
          <div className="w-16 h-1 bg-gray-300"></div>
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 bg-gray-300 text-white rounded-full flex items-center justify-center font-semibold">
              3
            </div>
            <span className="text-sm font-medium text-gray-400">Role</span>
          </div>
          <div className="w-16 h-1 bg-gray-300"></div>
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 bg-gray-300 text-white rounded-full flex items-center justify-center font-semibold">
              4
            </div>
            <span className="text-sm font-medium text-gray-400">Assessment</span>
          </div>
        </div>

        {/* Framework Cards */}
        <div className="grid md:grid-cols-2 gap-6">
          {frameworks.map((framework) => (
            <div
              key={framework.id}
              className={`bg-white rounded-2xl shadow-lg p-8 transition-all ${
                framework.enabled
                  ? 'screen-card cursor-pointer hover:shadow-2xl'
                  : 'opacity-60 cursor-not-allowed'
              }`}
              onClick={() => framework.enabled && onSelect(framework.id)}
            >
              <div className="flex items-start justify-between mb-4">
                <div className="text-5xl mb-2">{framework.icon}</div>
                {!framework.enabled && (
                  <div className="flex items-center gap-1 bg-gray-100 text-gray-600 px-3 py-1 rounded-full text-sm">
                    <Lock className="w-3 h-3" />
                    Coming Soon
                  </div>
                )}
              </div>
              
              <h3 className="text-2xl font-bold mb-2">{framework.name}</h3>
              <p className="text-lg text-indigo-600 font-semibold mb-3">
                {framework.fullName}
              </p>
              <p className="text-gray-600 mb-4">{framework.description}</p>
              
              {framework.enabled && (
                <div className="flex items-center gap-2 text-sm">
                  <Flag className="w-4 h-4 text-green-600" />
                  <span className="font-semibold text-green-600">
                    {framework.questions} questions available
                  </span>
                </div>
              )}
            </div>
          ))}
        </div>

        {/* Info Box */}
        <div className="mt-12 bg-blue-50 border-2 border-blue-200 rounded-xl p-6">
          <div className="flex items-start gap-4">
            <div className="w-10 h-10 bg-blue-500 rounded-lg flex items-center justify-center flex-shrink-0">
              <span className="text-white text-xl">💡</span>
            </div>
            <div>
              <h4 className="font-bold text-lg mb-2 text-blue-900">
                Currently Available: Canadian Framework
              </h4>
              <p className="text-blue-800">
                We're currently supporting the Canadian AI regulatory framework based on AIDA (Bill C-27).
                Additional frameworks (EU, US, UK) will be available soon. Each framework is tailored to
                its specific regulatory requirements and best practices.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FrameworkSelection;
